import {
  MetorialDashboardOrganizationsAuditLogsEndpoint,
  type DashboardOrganizationsAuditLogsListOutput,
  type MetorialDashboardSDK
} from '@metorial/dashboard-sdk';
import { createLoader } from '@metorial/data-hooks';
import { usePaginator } from '../../lib/usePaginator';
import { withAuth } from '../../user';

export type AuditLog = DashboardOrganizationsAuditLogsListOutput['items'][number];

// `GET /dashboard/organizations/:org/audit-logs` is generated in the SDK but not
// wired onto the SDK object yet. Build the endpoint from the shared manager until
// it is (any endpoint instance carries it).
let auditLogsEndpoint = (sdk: MetorialDashboardSDK) => {
  let wired = (sdk as any).auditLogs as MetorialDashboardOrganizationsAuditLogsEndpoint | undefined;
  if (wired) return wired;

  let manager = (sdk.auditLogStreams as any)._manager;
  return new MetorialDashboardOrganizationsAuditLogsEndpoint(manager);
};

export let auditLogsLoader = createLoader({
  name: 'auditLogs',
  fetch: (i: { organizationId: string; before?: string; after?: string; limit?: number }) =>
    withAuth(sdk =>
      auditLogsEndpoint(sdk).list(i.organizationId, {
        order: 'desc',
        limit: i.limit ?? 50,
        before: i.before,
        after: i.after
      })
    ),
  mutators: {}
});

export let useAuditLogs = (
  organizationId: string | null | undefined,
  opts?: { limit?: number }
) =>
  usePaginator(cursor =>
    auditLogsLoader.use(
      organizationId ? { organizationId, limit: opts?.limit, ...cursor } : null
    )
  );

export let auditLogLoader = createLoader({
  name: 'auditLog',
  parents: [auditLogsLoader],
  fetch: (i: { organizationId: string; auditLogId: string }) =>
    withAuth(sdk => auditLogsEndpoint(sdk).get(i.organizationId, i.auditLogId)),
  mutators: {}
});

export let useAuditLog = (
  organizationId: string | null | undefined,
  auditLogId: string | null | undefined
) => auditLogLoader.use(organizationId && auditLogId ? { organizationId, auditLogId } : null);
